import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Link2, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { brl, apiError } from "@/lib/format";

export default function EventConfig() {
  const [event, setEvent] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/event").then((r) => { setEvent((prev) => prev || r.data.event); setCats(r.data.categories); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setEvent((e) => ({ ...e, [k]: v }));

  const paymentAmounts = (() => {
    const map = {};
    cats.forEach((c) => {
      const full = Number(c.price).toFixed(2);
      map[full] = map[full] || new Set();
      map[full].add(c.sport_type === "KIDS" ? "Kids" : c.sport_type === "CICLISMO" ? "Ciclismo" : "Corrida");
      if (c.sport_type !== "KIDS") {
        const disc = (Number(c.price) * 0.9).toFixed(2);
        map[disc] = map[disc] || new Set();
        map[disc].add(`${c.sport_type === "CICLISMO" ? "Ciclismo" : "Corrida"} c/ cupom 10%`);
      }
    });
    return Object.keys(map).sort((a, b) => parseFloat(b) - parseFloat(a)).map((k) => ({ key: k, label: [...map[k]].join(" • ") }));
  })();

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/event", {
        title: event.title, event_date: event.event_date, event_time: event.event_time, contact_phone: event.contact_phone,
        status: event.status, payment_links: event.payment_links || {},
        regulation: event.regulation, regulation_url: event.regulation_url, site_url: event.site_url, location: event.location, schedule: event.schedule, kit_info: event.kit_info, route_info: event.route_info,
      });
      toast.success("Configurações salvas!");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveCat = async (c, field, value) => {
    try { await api.put(`/admin/categories/${c.id}`, { [field]: field === "price" ? parseFloat(value) : parseInt(value) }); toast.success("Modalidade atualizada"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!event) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-config">
      <div>
        <p className="text-brand font-bold uppercase tracking-widest text-xs">Configurações</p>
        <h1 className="font-display text-4xl font-black uppercase">Evento & Pagamentos</h1>
      </div>

      <div className="rounded-2xl bg-surface border border-white/10 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2"><Label className="text-slate-300">Título do Evento</Label><Input data-testid="cfg-title" value={event.title || ""} onChange={(e) => set("title", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div><Label className="text-slate-300 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Data do Evento</Label><Input data-testid="cfg-date" type="date" value={(event.event_date || "").slice(0, 10)} onChange={(e) => set("event_date", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div><Label className="text-slate-300">Horário</Label><Input data-testid="cfg-time" type="time" value={event.event_time || ""} onChange={(e) => set("event_time", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-slate-300">Telefone para Informações e Inscrições</Label><Input data-testid="cfg-phone" value={event.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div><Label className="text-slate-300">Status Inscrições</Label>
            <Select value={event.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="cfg-status" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                <SelectItem value="OPEN">Abertas</SelectItem><SelectItem value="CLOSED">Encerradas</SelectItem>
              </SelectContent>
            </Select></div>
        </div>
        <div className="rounded-xl border border-white/10 bg-ink/60 p-4 space-y-3" data-testid="payment-links-panel">
          <div>
            <Label className="text-slate-200 font-bold flex items-center gap-1.5"><Link2 className="w-4 h-4 text-brand" /> Links de Pagamento Nubank — por valor final</Label>
            <p className="text-xs text-slate-500 mt-1">O atleta recebe o link correspondente ao valor exato da inscrição (modalidade + cupom). Valores sem link mostram "indisponível".</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {paymentAmounts.map((a) => (
              <div key={a.key} className={`rounded-lg border p-3 ${event.payment_links?.[a.key] ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`paylink-row-${a.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-display font-black text-lg">{brl(parseFloat(a.key))}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400">{a.label}</span>
                </div>
                <Input data-testid={`paylink-input-${a.key}`} placeholder="https://checkout.nubank.com.br/..." value={event.payment_links?.[a.key] || ""}
                  onChange={(e) => setEvent((ev) => ({ ...ev, payment_links: { ...(ev.payment_links || {}), [a.key]: e.target.value.trim() } }))} className="bg-ink border-white/10 font-mono text-xs" />
              </div>
            ))}
          </div>
        </div>
        <div><Label className="text-slate-300">Cronograma</Label><Textarea value={event.schedule || ""} onChange={(e) => set("schedule", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-slate-300">Retirada de Kits</Label><Textarea value={event.kit_info || ""} onChange={(e) => set("kit_info", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div><Label className="text-slate-300">Percurso</Label><Textarea value={event.route_info || ""} onChange={(e) => set("route_info", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
        </div>
        <div><Label className="text-slate-300">Regulamento (resumo)</Label><Textarea value={event.regulation || ""} onChange={(e) => set("regulation", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="text-slate-300">Link do Regulamento (PDF)</Label><Input data-testid="cfg-regulation-url" value={event.regulation_url || ""} onChange={(e) => set("regulation_url", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div><Label className="text-slate-300">Site do evento (exibido na página)</Label><Input data-testid="cfg-site-url" value={event.site_url || ""} onChange={(e) => set("site_url", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
          <div><Label className="text-slate-300">Local de Largada/Chegada</Label><Input data-testid="cfg-location" value={event.location || ""} onChange={(e) => set("location", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
        </div>
        <Button data-testid="cfg-save-btn" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Salvar Configurações</>}
        </Button>
      </div>

      {/* Categories / prices / slots */}
      <div className="rounded-2xl bg-surface border border-white/10 p-6">
        <h3 className="font-display text-xl uppercase font-bold mb-4">Modalidades, Valores e Vagas</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
              <th className="px-3 py-2">Modalidade</th><th className="px-3 py-2">Prefixo</th><th className="px-3 py-2">Valor (R$)</th><th className="px-3 py-2">Vagas</th><th className="px-3 py-2">Restantes</th>
            </tr></thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className="border-b border-white/5">
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-neon text-xs">{c.code_prefix}</td>
                  <td className="px-3 py-2">
                    <Input data-testid={`cat-price-${c.code_prefix}`} type="number" defaultValue={c.price}
                      onBlur={(e) => Number(e.target.value) !== Number(c.price) && saveCat(c, "price", e.target.value)}
                      className="bg-ink border-white/10 h-8 w-24" />
                  </td>
                  <td className="px-3 py-2">
                    <Input data-testid={`cat-slots-${c.code_prefix}`} type="number" defaultValue={c.slots_total}
                      onBlur={(e) => Number(e.target.value) !== Number(c.slots_total) && saveCat(c, "slots_total", e.target.value)}
                      className="bg-ink border-white/10 h-8 w-20" />
                  </td>
                  <td className="px-3 py-2 text-slate-400">{c.remaining}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
